import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, Trash2, Plus, Minus, Search, Receipt, Cloud } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { uploadPdfToDrive } from "@/lib/drive";
import { ShiftBanner } from "@/components/ShiftBanner";

const CATS = ["all", "meat", "fish", "vegetables", "fruits"];
const PAYMENT_METHODS = ["cash", "bank", "gcash", "maya", "cheque"];

export default function PosPage() {
    const [farm, setFarm] = useState({ currency: "PHP", name: "AJ's Farm" });
    const [products, setProducts] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [cart, setCart] = useState([]); // {product_id, name, unit, unit_price, quantity, subtotal, stock}
    const [customerId, setCustomerId] = useState("");
    const [customerName, setCustomerName] = useState("");
    const [tax, setTax] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [payMethod, setPayMethod] = useState("cash");
    const [payAmount, setPayAmount] = useState("");
    const [reference, setReference] = useState("");
    const [processing, setProcessing] = useState(false);
    const [receipt, setReceipt] = useState(null);
    const [shift, setShift] = useState(null);
    const [animalId, setAnimalId] = useState("");
    const [animalPrice, setAnimalPrice] = useState("");

    const load = async () => {
        const [f, p, a, c, s] = await Promise.all([
            api.get("/farm").then(r => r.data),
            api.get("/products").then(r => r.data),
            api.get("/animals").then(r => r.data).catch(() => []),
            api.get("/customers").then(r => r.data),
            api.get("/pos/shifts/current").then(r => r.data).catch(() => ({})),
        ]);
        setFarm(f); setProducts(p); setAnimals(a); setCustomers(c);
        setShift(s && s.id ? s : null);
    };
    useEffect(() => { load(); }, []);
    const shiftLocked = !shift;  // require open shift before selling

    const cur = (v) => formatCurrency(v, farm.currency);

    const filtered = useMemo(() => {
        const ql = q.toLowerCase();
        return products.filter(p =>
            (cat === "all" || p.category === cat) &&
            (!ql || (p.name || "").toLowerCase().includes(ql) || (p.sku || "").toLowerCase().includes(ql))
        );
    }, [products, q, cat]);

    const addToCart = (p) => {
        setCart(prev => {
            const ex = prev.find(x => x.product_id === p.id);
            if (ex) {
                if (ex.quantity + 1 > Number(p.current_stock || 0)) {
                    toast.error(`Only ${p.current_stock} ${p.unit} in stock`);
                    return prev;
                }
                return prev.map(x => x.product_id === p.id ? { ...x, quantity: x.quantity + 1, subtotal: (x.quantity + 1) * x.unit_price } : x);
            }
            if (Number(p.current_stock || 0) <= 0) { toast.error("Out of stock"); return prev; }
            return [...prev, {
                product_id: p.id, name: p.name, sku: p.sku, unit: p.unit,
                unit_price: Number(p.unit_price || 0), quantity: 1,
                subtotal: Number(p.unit_price || 0), stock: Number(p.current_stock || 0),
            }];
        });
    };
    const addAnimalToCart = () => {
        const animal = animals.find((entry) => entry.id === animalId);
        if (!animal) {
            toast.error("Select an animal first");
            return;
        }
        const price = Number(animalPrice || 0);
        if (price <= 0) {
            toast.error("Enter a valid sale price");
            return;
        }
        setCart(prev => {
            if (prev.some(item => item.animal_id === animal.id)) {
                toast.error("Animal already in cart");
                return prev;
            }
            return [...prev, {
                animal_id: animal.id,
                name: animal.tag_id || animal.name || animal.id,
                sku: animal.tag_id || animal.id,
                unit: "head",
                unit_price: price,
                quantity: 1,
                subtotal: price,
                stock: 1,
            }];
        });
        setAnimalId("");
        setAnimalPrice("");
    };
    const changeQty = (pid, delta) => {
        setCart(prev => prev.map(x => {
            if (x.product_id !== pid) return x;
            const newQ = x.quantity + delta;
            if (newQ <= 0) return null;
            if (newQ > x.stock) { toast.error(`Only ${x.stock} in stock`); return x; }
            return { ...x, quantity: newQ, subtotal: newQ * x.unit_price };
        }).filter(Boolean));
    };
    const removeItem = (id) => setCart(prev => prev.filter(x => (x.product_id || x.animal_id) !== id));
    const clearCart = () => { setCart([]); setTax(0); setDiscount(0); setPayAmount(""); setReference(""); setCustomerId(""); setCustomerName(""); setAnimalId(""); setAnimalPrice(""); };

    const subtotal = cart.reduce((s, x) => s + x.subtotal, 0);
    const grandTotal = Math.max(subtotal + Number(tax || 0) - Number(discount || 0), 0);

    const checkout = async (paidNow = true) => {
        if (cart.length === 0) { toast.error("Cart is empty"); return; }
        setProcessing(true);
        try {
            const paymentAmount = paidNow ? (Number(payAmount || grandTotal)) : 0;
            const isWalkin = !customerId || customerId === "__walkin__";
            const body = {
                customer_id: isWalkin ? null : customerId,
                customer_name: isWalkin ? (customerName || "Walk-in") : (customers.find(c => c.id === customerId)?.name || ""),
                items: cart.map(x => ({ product_id: x.product_id, animal_id: x.animal_id, name: x.name, quantity: x.quantity, unit_price: x.unit_price })),
                tax: Number(tax || 0),
                discount: Number(discount || 0),
                notes: "",
            };
            if (paidNow) {
                body.payment = { amount: paymentAmount, method: payMethod, reference };
            }
            const r = await api.post("/pos/sell", body);
            toast.success(paidNow ? "Sale complete! Stock deducted." : "Invoice created (unpaid).");
            setReceipt({ ...r.data, farm });
            clearCart();
            load(); // refresh stock
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to process sale");
        } finally { setProcessing(false); }
    };

    const buildReceiptDoc = () => {
        if (!receipt) return null;
        const doc = new jsPDF({ unit: "mm", format: [80, 200] });
        let y = 8;
        doc.setFontSize(11); doc.setFont(undefined, "bold");
        doc.text(farm.name || "Farm", 40, y, { align: "center" }); y += 5;
        doc.setFontSize(8); doc.setFont(undefined, "normal");
        doc.text("Sales Receipt", 40, y, { align: "center" }); y += 4;
        doc.text(`${receipt.invoice_number}`, 40, y, { align: "center" }); y += 3;
        doc.text(`${new Date(receipt.created_at).toLocaleString()}`, 40, y, { align: "center" }); y += 5;
        doc.text(`Customer: ${receipt.customer_name}`, 4, y); y += 4;
        doc.line(4, y, 76, y); y += 3;
        receipt.items.forEach(it => {
            doc.text(`${it.name}`, 4, y);
            doc.text(`${it.quantity} x ${cur(it.unit_price)}`, 76, y, { align: "right" }); y += 4;
            doc.text(cur(it.subtotal), 76, y, { align: "right" }); y += 4;
        });
        doc.line(4, y, 76, y); y += 4;
        doc.text("Subtotal:", 4, y); doc.text(cur(receipt.subtotal), 76, y, { align: "right" }); y += 4;
        if (receipt.tax) { doc.text("Tax:", 4, y); doc.text(cur(receipt.tax), 76, y, { align: "right" }); y += 4; }
        if (receipt.discount) { doc.text("Discount:", 4, y); doc.text(`-${cur(receipt.discount)}`, 76, y, { align: "right" }); y += 4; }
        doc.setFont(undefined, "bold");
        doc.text("TOTAL:", 4, y); doc.text(cur(receipt.grand_total), 76, y, { align: "right" }); y += 5;
        doc.setFont(undefined, "normal");
        doc.text(`Status: ${receipt.status.toUpperCase()}`, 4, y); y += 4;
        if (receipt.total_paid) { doc.text(`Paid: ${cur(receipt.total_paid)}`, 4, y); y += 4; }
        y += 3;
        doc.text("Thank you!", 40, y, { align: "center" });
        return doc;
    };

    const printReceipt = () => {
        const doc = buildReceiptDoc();
        if (doc) doc.save(`${receipt.invoice_number}.pdf`);
    };

    const sendReceiptToDrive = async () => {
        const doc = buildReceiptDoc();
        if (!doc) return;
        try { await uploadPdfToDrive(doc, `${receipt.invoice_number}.pdf`, "Receipts"); }
        catch (e) { console.error("Drive receipt upload failed:", e); /* toast shown by helper */ }
    };

    return (
        <div className="space-y-6" data-testid="pos-page">
            <div>
                <div className="tiny-label">{i18n.t("batch3.fa3cf2ad51e0")}</div>
                <h1 className="text-4xl font-bold tracking-tight">{i18n.t("batch3.0bebea9f7887")}</h1>
                <p className="text-muted-foreground mt-1">{i18n.t("batch3.e3ea0776251e")}</p>
            </div>

            <ShiftBanner onChanged={load} />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Products picker */}
                <div className="lg:col-span-2 space-y-3">
                    <Card className="p-3 flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-3.5 w-4 h-4 text-muted-foreground" />
                            <Input value={q} onChange={e => setQ(e.target.value)} placeholder={i18n.t("batch3.e3f5f6946da2")} className="h-11 pl-10" data-testid="pos-search-input" />
                        </div>
                        <div className="flex gap-1 flex-wrap">
                            {CATS.map(c => (
                                <Button key={c} size="sm" variant={cat === c ? "default" : "outline"} onClick={() => setCat(c)} className="h-9 capitalize" data-testid={`pos-cat-${c}`}>{c}</Button>
                            ))}
                        </div>
                    </Card>

                    <Card className="p-3">
                        <div className="tiny-label mb-2">{i18n.t("batch3.bfa4d6fa106b")}</div>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_auto] gap-2 items-end">
                            <div>
                                <Label className="tiny-label">{i18n.t("batch3.7389b3b752fa")}</Label>
                                <Select value={animalId} onValueChange={setAnimalId}>
                                    <SelectTrigger className="h-11 mt-1" data-testid="pos-animal-select"><SelectValue placeholder={i18n.t("batch3.be6ffb598e6a")} /></SelectTrigger>
                                    <SelectContent>
                                        {animals.filter(a => !["sold", "deceased", "culled"].includes(String(a.status || "").toLowerCase())).map(a => (
                                            <SelectItem key={a.id} value={a.id}>{`${a.tag_id || a.name || a.id} · ${a.status || "active"}`}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch3.17ae734e917c")}</Label>
                                <Input type="number" value={animalPrice} onChange={e => setAnimalPrice(e.target.value)} className="h-11 mt-1" data-testid="pos-animal-price-input" />
                            </div>
                            <Button type="button" className="h-11" onClick={addAnimalToCart} data-testid="pos-add-animal-btn">{i18n.t("batch3.22d5a3425998")}</Button>
                        </div>
                    </Card>

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {filtered.map(p => {
                            const low = Number(p.current_stock || 0) <= Number(p.reorder_level || 0);
                            return (
                                <button key={p.id} onClick={() => addToCart(p)} disabled={Number(p.current_stock || 0) <= 0}
                                    data-testid={`pos-product-${p.id}`}
                                    className={`text-left p-3 rounded-2xl border transition-all hover:shadow-lg hover:-translate-y-0.5 active:scale-95 ${Number(p.current_stock || 0) <= 0 ? "opacity-40 cursor-not-allowed bg-secondary" : "bg-card hover:border-primary"}`}>
                                    <div className="font-semibold text-sm line-clamp-2 leading-tight">{p.name}</div>
                                    <div className="text-[10px] tracking-widest uppercase mt-1 text-muted-foreground">{p.category} · {p.unit}</div>
                                    <div className="mt-2 flex justify-between items-end">
                                        <div className="font-mono font-bold text-primary">{cur(p.unit_price)}</div>
                                        <div className={`text-xs font-mono ${low ? "text-destructive font-bold" : "text-muted-foreground"}`}>{p.current_stock} {p.unit}</div>
                                    </div>
                                </button>
                            );
                        })}
                        {filtered.length === 0 && <div className="col-span-full py-10 text-center text-muted-foreground text-sm">{i18n.t("batch3.304f2a6def00")}</div>}
                    </div>
                </div>

                {/* Cart / Checkout */}
                <Card className="p-4 h-fit sticky top-4">
                    <div className="flex items-center gap-2 mb-3">
                        <ShoppingCart className="w-5 h-5 text-primary" />
                        <h3 className="font-semibold text-lg">{i18n.t("batch3.ec8a638c20f0")}{cart.length})</h3>
                        {cart.length > 0 && <Button size="sm" variant="ghost" onClick={clearCart} className="ml-auto text-xs" data-testid="pos-clear-btn">{i18n.t("batch3.719ea396ad92")}</Button>}
                    </div>

                    <div className="space-y-2 max-h-64 overflow-y-auto">
                        {cart.map(it => (
                            <div key={it.product_id || it.animal_id} className="flex items-center gap-2 text-sm border-b pb-2">
                                <div className="flex-1 min-w-0">
                                    <div className="font-semibold truncate">{it.name}</div>
                                    <div className="text-xs text-muted-foreground font-mono">{cur(it.unit_price)} × {it.quantity} {it.unit}</div>
                                </div>
                                {it.product_id ? (
                                    <div className="flex items-center gap-1">
                                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(it.product_id, -1)} data-testid={`pos-minus-${it.product_id}`}><Minus className="w-3 h-3" /></Button>
                                        <span className="font-mono font-bold w-6 text-center">{it.quantity}</span>
                                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => changeQty(it.product_id, 1)} data-testid={`pos-plus-${it.product_id}`}><Plus className="w-3 h-3" /></Button>
                                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(it.product_id)}><Trash2 className="w-3 h-3" /></Button>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-1">
                                        <span className="font-mono font-bold w-6 text-center">1</span>
                                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeItem(it.animal_id)}><Trash2 className="w-3 h-3" /></Button>
                                    </div>
                                )}
                                <div className="font-mono font-bold w-20 text-right">{cur(it.subtotal)}</div>
                            </div>
                        ))}
                        {cart.length === 0 && <div className="text-center py-6 text-sm text-muted-foreground">{i18n.t("batch3.83c81d7a5d24")}</div>}
                    </div>

                    {cart.length > 0 && <>
                        <div className="mt-3 space-y-2 border-t pt-3">
                            <div>
                                <Label className="tiny-label">{i18n.t("batch3.0e85749a6f40")}</Label>
                                <Select value={customerId} onValueChange={setCustomerId}>
                                    <SelectTrigger className="h-10 mt-1" data-testid="pos-customer-select"><SelectValue placeholder={i18n.t("batch3.54376586d240")} /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__walkin__">{i18n.t("batch3.54376586d240")}</SelectItem>
                                        {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                                {(!customerId || customerId === "__walkin__") && <Input placeholder={i18n.t("batch3.46cf37e16197")} value={customerName} onChange={e => setCustomerName(e.target.value)} className="h-10 mt-2" />}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div><Label className="tiny-label">{i18n.t("batch3.9be70f66f8dd")}</Label><Input type="number" value={tax} onChange={e => setTax(e.target.value)} className="h-10 mt-1 font-mono" /></div>
                                <div><Label className="tiny-label">{i18n.t("batch3.b524936d7aa1")}</Label><Input type="number" value={discount} onChange={e => setDiscount(e.target.value)} className="h-10 mt-1 font-mono" /></div>
                            </div>
                            <div className="rounded-xl bg-secondary p-3 space-y-1 text-sm">
                                <div className="flex justify-between"><span>{i18n.t("batch3.97f7359ed801")}</span><span className="font-mono">{cur(subtotal)}</span></div>
                                {Number(tax || 0) > 0 && <div className="flex justify-between"><span>{i18n.t("batch3.9be70f66f8dd")}</span><span className="font-mono">{cur(tax)}</span></div>}
                                {Number(discount || 0) > 0 && <div className="flex justify-between text-destructive"><span>{i18n.t("batch3.b524936d7aa1")}</span><span className="font-mono">−{cur(discount)}</span></div>}
                                <div className="flex justify-between font-bold text-lg pt-1 border-t border-border">
                                    <span>{i18n.t("batch3.b25928c69902")}</span><span className="font-mono text-primary">{cur(grandTotal)}</span>
                                </div>
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch3.f383f6a23c87")}</Label>
                                <Select value={payMethod} onValueChange={setPayMethod}>
                                    <SelectTrigger className="h-10 mt-1" data-testid="pos-method-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PAYMENT_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="tiny-label">{i18n.t("batch3.2a6590d41767")}</Label>
                                <Input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder={grandTotal.toFixed(2)} className="h-11 mt-1 font-mono text-lg" data-testid="pos-amount-input" />
                            </div>
                            {payMethod !== "cash" && <Input placeholder={i18n.t("batch3.2b7336c8b9c9")} value={reference} onChange={e => setReference(e.target.value)} className="h-10" />}
                            {payAmount && Number(payAmount) > grandTotal && (
                                <div className="text-xs bg-primary/10 text-primary rounded-lg p-2 font-mono flex justify-between">
                                    <span>{i18n.t("batch3.07b146812266")}</span><span className="font-bold">{cur(Number(payAmount) - grandTotal)}</span>
                                </div>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            <Button variant="outline" onClick={() => checkout(false)} disabled={processing || shiftLocked} className="h-12" data-testid="pos-invoice-only-btn">{i18n.t("batch3.f2302c88a11a")}</Button>
                            <Button onClick={() => checkout(true)} disabled={processing || shiftLocked} className="h-12 font-bold" data-testid="pos-checkout-btn">
                                {processing ? "..." : shiftLocked ? "Open Shift First" : `Charge ${cur(grandTotal)}`}
                            </Button>
                        </div>
                    </>}
                </Card>
            </div>

            {/* Receipt dialog */}
            <Dialog open={!!receipt} onOpenChange={o => !o && setReceipt(null)}>
                <DialogContent data-testid="pos-receipt-dialog">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="w-5 h-5 text-primary" /> {i18n.t("batch3.4b1f23f98e4f")}</DialogTitle></DialogHeader>
                    {receipt && <div className="space-y-3 text-sm">
                        <div className="text-center border-b pb-3">
                            <div className="font-bold">{receipt.invoice_number}</div>
                            <div className="text-xs text-muted-foreground">{new Date(receipt.created_at).toLocaleString()}</div>
                        </div>
                        <div>{i18n.t("batch3.5e0d7363a956")} <span className="font-semibold">{receipt.customer_name}</span></div>
                        <div className="space-y-1">
                            {receipt.items.map((it, idx) => (
                                <div key={it.product_id || `${idx}-${it.name}`} className="flex justify-between text-xs">
                                    <span>{it.name} × {it.quantity}</span>
                                    <span className="font-mono">{cur(it.subtotal)}</span>
                                </div>
                            ))}
                        </div>
                        <div className="border-t pt-2 font-mono">
                            <div className="flex justify-between"><span>{i18n.t("batch3.97f7359ed801")}</span><span>{cur(receipt.subtotal)}</span></div>
                            {receipt.tax > 0 && <div className="flex justify-between"><span>{i18n.t("batch3.9be70f66f8dd")}</span><span>{cur(receipt.tax)}</span></div>}
                            {receipt.discount > 0 && <div className="flex justify-between"><span>{i18n.t("batch3.b524936d7aa1")}</span><span>−{cur(receipt.discount)}</span></div>}
                            <div className="flex justify-between font-bold text-lg text-primary"><span>{i18n.t("batch3.5c6505ae3c86")}</span><span>{cur(receipt.grand_total)}</span></div>
                            <div className="flex justify-between mt-2"><span>{i18n.t("batch3.bae7d5be7082")}</span><span className="uppercase font-bold">{receipt.status}</span></div>
                            {receipt.total_paid > 0 && <div className="flex justify-between"><span>{i18n.t("batch3.dc9d4584a554")}</span><span>{cur(receipt.total_paid)}</span></div>}
                            {receipt.change_given > 0 && <div className="flex justify-between text-primary font-bold"><span>{i18n.t("batch3.64fbd995d3b6")}</span><span>{cur(receipt.change_given)}</span></div>}
                        </div>
                        <Button onClick={printReceipt} className="w-full h-11" data-testid="pos-print-receipt-btn"><Receipt className="w-4 h-4 mr-2" />{i18n.t("batch3.a4a52407a5c6")}</Button>
                        <Button onClick={sendReceiptToDrive} variant="outline" className="w-full h-11" data-testid="pos-drive-receipt-btn"><Cloud className="w-4 h-4 mr-2" />{i18n.t("batch3.127c03434417")}</Button>
                    </div>}
                </DialogContent>
            </Dialog>
        </div>
    );
}
